import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Bot, User, Sparkles, Loader2, Check } from 'lucide-react';
import { ScrollReveal } from '@/components/ui/ScrollReveal';
import { Container, Section } from '@/components/layout/Container';
import { resolveApiBaseUrl } from '@/lib/utils';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

const mockConversations: { user: string; assistant: string }[] = [
  {
    user: "What kind of advisory roles do you take on?",
    assistant: "I engage in board-level medical advisory for healthcare systems, MedTech companies, and AI-health developers. My focus is on strategic infrastructure, regulatory navigation, and clinical workflow optimization at the executive level."
  },
  {
    user: "Can you help with AI integration in our healthcare platform?",
    assistant: "Absolutely. I bridge clinical and engineering teams, having built multiple AI-assisted healthcare tools myself. I can advise on workflow integration, regulatory compliance, and technical architecture for healthcare AI implementations."
  },
  {
    user: "What is your experience with emergency medicine?",
    assistant: "Emergency medicine has been a defining component of my career. I've worked thousands of hours in high-pressure environments, combining full-time general practice with extensive emergency duty work. This shapes my decision-making speed and operational clarity."
  },
  {
    user: "Do you work internationally?",
    assistant: "Yes, I operate globally and am open to EU engagements, US-based advisory, Asian markets, and international travel. I speak Norwegian and English fluently, Polish near-fluently, and have working proficiency in German."
  }
];

const fallbackSuggestedQuestions = [
  "What advisory roles do you take on?",
  "What is your medical specialty?",
  "What is your emergency medicine experience?",
  "Can you help with AI integration in healthcare products?",
  "Can you bridge clinicians and engineers?",
  "What technologies do you work with?",
  "What does board-level advisory mean in your work?",
  "Which organizations do you usually support?",
  "Which languages do you speak?",
  "How do we start an engagement?",
  "Do you have OpenAI API experience?",
  "Do you work internationally?",
];

const API_URL = resolveApiBaseUrl(import.meta.env.VITE_API_URL);
const CHAT_ENABLED = (import.meta.env.VITE_CHAT_ENABLED ?? 'true').toLowerCase() === 'true';
const REQUEST_TIMEOUT_MS = 30000;
const MAX_HISTORY_MESSAGES = 8;
const SEEDED_DELAY_MS_MIN = 800;
const SEEDED_DELAY_MS_MAX = 1800;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const normalizeQuestion = (text: string) => text.trim().toLowerCase();

const getMockResponse = (text: string): string => {
  const normalized = text.toLowerCase().trim();
  const match = mockConversations.find((conversation) => {
    const prefix = conversation.user.toLowerCase().slice(0, 24);
    return normalized.includes(prefix) || prefix.includes(normalized);
  });

  return (
    match?.assistant ||
    'I can help with advisory services, medical background, AI integration capability, and engagement options. For a concrete discussion, please use the contact form.'
  );
};

const getHistoryPayload = (
  history: Message[]
): Array<{ role: 'user' | 'assistant'; content: string }> =>
  history
    .filter((message) => message.content.trim().length > 0)
    .slice(-MAX_HISTORY_MESSAGES)
    .map(({ role, content }) => ({ role, content }));

const fetchAssistantReply = async (
  message: string,
  history: Message[]
): Promise<{ response: string; source: string }> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(`${API_URL}/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message,
        history: getHistoryPayload(history),
      }),
      signal: controller.signal,
    });

    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(payload?.error || 'Chat request failed');
    }

    const content = payload?.response;
    if (typeof content !== 'string' || !content.trim()) {
      throw new Error('Invalid response from chat backend');
    }

    return {
      response: content.trim(),
      source: typeof payload?.source === 'string' ? payload.source : 'unknown',
    };
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export const AiChatDemo: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: 'Hello. I\'m Christer. Ask me about my advisory services, clinical background, or technical capabilities.',
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>(
    fallbackSuggestedQuestions
  );
  const [usedSuggestedQuestions, setUsedSuggestedQuestions] = useState<Set<string>>(
    new Set()
  );
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const loadSeededQuestions = async () => {
      try {
        const response = await fetch(`${API_URL}/chat/seeded-questions?limit=12`);
        if (!response.ok) return;
        const payload = await response.json();
        if (Array.isArray(payload?.questions) && payload.questions.length > 0) {
          setSuggestedQuestions(
            payload.questions.filter((question: unknown) => typeof question === 'string')
          );
        }
      } catch {
        // Keep local fallback list.
      }
    };

    loadSeededQuestions();
  }, []);

  const handleSend = async (text: string) => {
    if (!text.trim() || isTyping) return;
    const cleanText = text.trim();
    const normalizedInput = normalizeQuestion(cleanText);
    const matchesSuggestedQuestion = suggestedQuestions.some(
      (question) => normalizeQuestion(question) === normalizedInput
    );

    if (matchesSuggestedQuestion) {
      setUsedSuggestedQuestions((prev) => {
        const next = new Set(prev);
        next.add(normalizedInput);
        return next;
      });
    }

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: cleanText,
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    try {
      let reply: string;
      if (CHAT_ENABLED) {
        const result = await fetchAssistantReply(cleanText, messages);
        if (result.source === 'seeded') {
          const extraDelay =
            SEEDED_DELAY_MS_MIN +
            Math.floor(Math.random() * (SEEDED_DELAY_MS_MAX - SEEDED_DELAY_MS_MIN + 1));
          await sleep(extraDelay);
        }
        reply = result.response;
      } else {
        // Keep a slight delay in demo mode to mimic backend latency.
        await sleep(800);
        reply = getMockResponse(cleanText);
      }

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: reply,
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      const errorText = error instanceof Error ? error.message : '';
      const fallbackMessage =
        errorText.includes('OPENAI_API_KEY')
          ? 'Chat is not configured yet. Add OPENAI_API_KEY in Konsulent/backend/.env and restart backend.'
          : errorText.includes('Too many chat requests')
            ? 'Too many chat requests from your IP right now. Please wait a minute and try again.'
            : errorText
              ? errorText
          : 'Chat is temporarily unavailable. Please try again, or use the contact form below.';

      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: fallbackMessage,
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSend(input);
  };

  return (
    <Section>
      <Container size="md">
        {/* Section Header */}
        <ScrollReveal className="text-center mb-12">
          <span className="text-caption text-apple-blue uppercase tracking-wider font-medium">
            AI Assistant
          </span>
          <h2 className="text-h1 md:text-display-m font-bold mt-4 mb-6">
            Experience the <span className="gradient-text">Interface</span>
          </h2>
          <p className="text-body-l text-text-secondary max-w-2xl mx-auto">
            This is a preview of how AI integration can enhance advisory interactions. 
            Ask questions about my background and services.
          </p>
        </ScrollReveal>

        {/* Chat Interface */}
        <ScrollReveal>
          <div className="bg-bg-tertiary rounded-3xl border border-border-subtle overflow-hidden shadow-2xl">
            {/* Chat Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle bg-bg-secondary/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-apple-blue/10 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-apple-blue" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary">AI Assistant</h3>
                  <p className="text-caption text-text-tertiary">Powered by OpenAI</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
                <span className="text-caption text-text-tertiary">Online</span>
              </div>
            </div>

            {/* Messages */}
            <div ref={messagesContainerRef} className="h-[400px] overflow-y-auto p-6 space-y-4">
              <AnimatePresence initial={false}>
                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
                  >
                    {/* Avatar */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.role === 'user' 
                        ? 'bg-apple-blue' 
                        : 'bg-bg-elevated border border-border-subtle'
                    }`}>
                      {message.role === 'user' ? (
                        <User className="w-4 h-4 text-white" />
                      ) : (
                        <Bot className="w-4 h-4 text-apple-blue" />
                      )}
                    </div>

                    {/* Message Bubble */}
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      message.role === 'user'
                        ? 'bg-apple-blue text-white'
                        : 'bg-bg-elevated border border-border-subtle text-text-primary'
                    }`}>
                      <p className="text-body-s leading-relaxed">{message.content}</p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {/* Typing Indicator */}
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex gap-3"
                >
                  <div className="w-8 h-8 rounded-full bg-bg-elevated border border-border-subtle flex items-center justify-center">
                    <Bot className="w-4 h-4 text-apple-blue" />
                  </div>
                  <div className="bg-bg-elevated border border-border-subtle rounded-2xl px-4 py-3">
                    <div className="flex gap-1">
                      <motion.div
                        animate={{ y: [0, -4, 0] }}
                        transition={{ repeat: Infinity, duration: 0.6 }}
                        className="w-2 h-2 rounded-full bg-text-tertiary"
                      />
                      <motion.div
                        animate={{ y: [0, -4, 0] }}
                        transition={{ repeat: Infinity, duration: 0.6, delay: 0.2 }}
                        className="w-2 h-2 rounded-full bg-text-tertiary"
                      />
                      <motion.div
                        animate={{ y: [0, -4, 0] }}
                        transition={{ repeat: Infinity, duration: 0.6, delay: 0.4 }}
                        className="w-2 h-2 rounded-full bg-text-tertiary"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Suggested Questions */}
            <div className="px-6 pb-2 flex flex-wrap gap-2">
              {suggestedQuestions.map((question) => {
                const isUsed = usedSuggestedQuestions.has(normalizeQuestion(question));
                return (
                  <button
                    key={question}
                    onClick={() => handleSend(question)}
                    disabled={isTyping || isUsed}
                    className={`text-caption px-3 py-1.5 rounded-full border transition-colors disabled:cursor-not-allowed ${
                      isUsed
                        ? 'bg-success/10 border-success/30 text-success/90'
                        : 'bg-bg-primary/50 border-border-subtle text-text-tertiary hover:text-text-primary hover:border-text-secondary disabled:opacity-50'
                    }`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {isUsed && <Check className="w-3 h-3" />}
                      {question}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Input */}
            <form onSubmit={handleSubmit} className="p-4 border-t border-border-subtle">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about advisory services..."
                  className="flex-1 bg-bg-primary border border-border-subtle rounded-full px-5 py-3 text-body text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-apple-blue focus:ring-1 focus:ring-apple-blue transition-all"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || isTyping}
                  className="w-12 h-12 rounded-full bg-apple-blue flex items-center justify-center text-white hover:bg-apple-blue-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isTyping ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Integration Note */}
          <div className="mt-6 flex items-center justify-center gap-2 text-caption text-text-tertiary">
            <Sparkles className="w-4 h-4" />
            <span>
              {CHAT_ENABLED
                ? 'Live OpenAI chat is enabled and scoped to advisory-related questions.'
                : 'Demo mode enabled. Set VITE_CHAT_ENABLED=true to use backend OpenAI chat.'}
            </span>
          </div>
        </ScrollReveal>
      </Container>
    </Section>
  );
};

export default AiChatDemo;
